import React, { useEffect, useState, useRef, useCallback } from "react";
import { SearchInput, Toast } from "../../Components";
import NotSubscribe from "../../Components/Base/Common/NotSubscribe";
import { storageHandler } from "../../Helpers/storage";
import JobCampaignsTable from "./JobCampaignsTable";
import {
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableRow,
} from "@mui/material";
import { Plus, Search, Loader2 } from "lucide-react";
import { useSidebar } from "../../Components/ui/sidebarNew";
import AppSelectInput from "../../Components/Base/Form/AppSelectInput";

// Shimmer row component for loading state
const TableRowShimmer = ({ index }) => (
  <TableRow
    sx={{
      "& td": {
        py: 1.5,
        px: 2.5,
        borderBottom: "1px solid #F3F4F6",
      },
      backgroundColor:
        index % 2 === 0 ? "rgba(248, 250, 252, 0.5)" : "transparent",
    }}
  >
    <TableCell sx={{ width: "40px", minWidth: "40px" }}>
      <div className="w-6 h-5 rounded bg-gray-200 animate-pulse" />
    </TableCell>
    <TableCell sx={{ minWidth: "150px" }}>
      <div className="h-4 w-3/4 rounded bg-gray-200 animate-pulse" />
    </TableCell>
    <TableCell sx={{ textAlign: "center", minWidth: "90px" }}>
      <div className="h-4 w-8 mx-auto rounded bg-gray-200 animate-pulse" />
    </TableCell>
    <TableCell sx={{ textAlign: "center", minWidth: "90px" }}>
      <div className="h-4 w-20 mx-auto rounded bg-gray-200 animate-pulse" />
    </TableCell>
    <TableCell sx={{ textAlign: "center", minWidth: "90px" }}>
      <div className="h-4 w-20 mx-auto rounded bg-gray-200 animate-pulse" />
    </TableCell>
    <TableCell sx={{ minWidth: "100px" }}>
      <div className="h-4 w-16 rounded bg-gray-200 animate-pulse" />
    </TableCell>
    <TableCell sx={{ textAlign: "center", minWidth: "80px" }}>
      <div className="h-6 w-14 mx-auto rounded-md bg-gray-200 animate-pulse" />
    </TableCell>
    <TableCell sx={{ minWidth: "60px" }}>
      <div className="h-6 w-6 mx-auto rounded bg-gray-200 animate-pulse" />
    </TableCell>
  </TableRow>
);

const selectDescList = [
  { id: "all", value: "All" },
  { id: "title", value: "Title" },
  { id: "status", value: "Status" },
];

export default function JobCampaignsMain({
  setTab,
  setModalCampaigns,
  setModalDataCampaigns,
  campaignData,
  paginationData,
  onPaginate,
  downloadCsv,
  sortList,
  sortOrder,
  search,
  setSearchTextOnChange,
  searchText,
  changeMediumDesc,
  setRecommandDataId,
  setCampaignId,
  onEditCampaign,
  onDeleteCampaign,
  onRefresh,
  onApplicationCountClick,
  onHeaderTitleChange,
  cartItem,
  closeCartPanel,
  setAuth,
  setModal,
  user,
  NotifModul,
  profiles,
  setModalNotification,
  locationData,
  goToPricing,
  selectDesc,
  isLoading,
  loadMore,
  hasMore,
  isLoadingMore,
  selectedTab,
  setSelectedTab,
  onStartChat,
  setOfferSelectedForReview,
}) {
  const [isUserHasSubscription, setUserHasSubscription] = useState(false);
  const [toastList, setToastList] = useState([]);
  const { state } = useSidebar();
  const tableContainerRef = useRef(null);
  const isLoadingRef = useRef(false);

  // Keep ref in sync with prop
  useEffect(() => {
    isLoadingRef.current = isLoadingMore;
  }, [isLoadingMore]);

  const selectedFilter =
    selectDescList.find((item) => item.id === selectDesc) || selectDescList[0];

  // Safety check for campaignData
  const safeCampaignData = Array.isArray(campaignData) ? campaignData : [];

  useEffect(() => {
    setUserHasSubscription(
      user !== null
        ? user.data.isUserHasSubscription === 1
          ? true
          : false
        : false
    );
  }, [user]);

  // Scroll handler for infinite scroll
  const handleScroll = useCallback(() => {
    const container = tableContainerRef.current;
    if (!container) {
      console.log("🔴 No container ref");
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = container;
    console.log(
      "📜 Scroll detected - scrollTop:",
      scrollTop,
      "clientHeight:",
      clientHeight,
      "scrollHeight:",
      scrollHeight,
      "nearBottom:",
      scrollTop + clientHeight >= scrollHeight - 150
    );

    // Use ref to get latest loading state
    if (isLoadingRef.current) {
      console.log("⏳ Still loading, skipping...");
      return;
    }

    if (!hasMore) {
      console.log("🛑 No more data to load");
      return;
    }

    // Load more when user scrolls near the bottom (within 150px)
    if (scrollTop + clientHeight >= scrollHeight - 150) {
      console.log("✅ Triggering loadMore!");
      if (loadMore) {
        loadMore();
      }
    }
  }, [hasMore, loadMore]);

  // Note: Using onScroll prop instead of addEventListener for better React compatibility

  let data = null;
  try {
    const userInfo = storageHandler.getUserInfo();
    if (userInfo) {
      const parsed = JSON.parse(userInfo);
      data = parsed?.data || null;
    }
  } catch (error) {
    console.error("Error parsing user info:", error);
  }

  return (
    <div
      style={{
        marginLeft: state === "collapsed" ? "28px" : "",
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 100px)", // Dynamic height based on viewport minus navbar
        overflow: "hidden",
      }}
    >
      {isUserHasSubscription && data.p_f_ids.includes(13) && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
          }}
        >
          {/* Header Section */}
          <header
            className="flex pb-4 flex-col md:flex-row md:items-center md:justify-between gap-3"
            style={{ flexShrink: 0, zIndex: 20, position: "relative" }}
          >
            {/* Tab Filters */}
            <ul className="flex flex-wrap items-center gap-4 m-0 p-0 list-none">
              {[
                { id: "all", label: "All" },
                { id: "public", label: "Public" },
                { id: "private", label: "Private" },
              ].map((tab) => (
                <li key={tab.id}>
                  <button
                    onClick={() => setSelectedTab(tab.id)}
                    className={`flex h-[38px] px-5 justify-center items-center rounded-full text-sm font-medium cursor-pointer transition-all duration-200  ${
                      selectedTab === tab.id
                        ? "bg-green-50 text-green-700 font-semibold shadow-sm border border-green-200"
                        : "bg-gray-50 text-gray-500 hover:bg-green-50 hover:text-green-700 border-none"
                    }`}
                    style={{
                      boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.08)",
                    }}
                  >
                    {tab.label}
                  </button>
                </li>
              ))}
            </ul>

            {/* Actions Section */}
            <div className="flex flex-wrap items-center gap-5 justify-center">
              {/* Filter Select */}
              <div
                style={{
                  width: "120px",
                  boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.08)",
                  borderRadius: "9999px",
                }}
              >
                <AppSelectInput
                  list={selectDescList}
                  selected={selectedFilter}
                  onChange={(value) => {
                    changeMediumDesc(value);
                  }}
                  className="!rounded-full !border-none !bg-gray-50"
                  style={{
                    paddingBottom: "0px",
                  }}
                />
              </div>

              {/* Search Input */}
              <div
                style={{
                  width: "280px",
                  boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.08)",
                  borderRadius: "9999px",
                }}
              >
                <SearchInput
                  value={searchText}
                  onClick={search}
                  type="text"
                  icon={<Search color="#9CA3AF" strokeWidth={2.2} size={18} />}
                  placeholder="Search campaigns..."
                  onChange={setSearchTextOnChange}
                  className="!rounded-full !border-none !bg-gray-50 pb-0"
                  style={{
                    paddingBottom: "0px",
                  }}
                />
              </div>

              {/* Add Campaign Button */}
              <button
                onClick={() => {
                  window.location.href = "/#/brand/jobpostmain";
                }}
                className="flex h-[38px] px-4 items-center gap-2 rounded-full text-sm font-semibold cursor-pointer border-none text-white transition-all duration-200 hover:opacity-90"
                style={{
                  background:
                    "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                  boxShadow: "0px 2px 6px 0px rgba(16, 185, 129, 0.25)",
                }}
              >
                <Plus strokeWidth={2.2} size={18} />
                <span>Add Campaign</span>
              </button>
            </div>
          </header>

          {/* Table Container - Uses flex to fill remaining screen height */}
          <div
            className="rounded-xl bg-white overflow-hidden w-full"
            style={{
              boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.08)",
              border: "1px solid rgba(16, 185, 129, 0.1)",
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0, // Important for flex child to allow shrinking
            }}
          >
            <div
              ref={tableContainerRef}
              onScroll={handleScroll}
              style={{
                flex: 1,
                overflowY: "auto",
                overflowX: "auto",
                scrollbarWidth: "thin",
                scrollbarColor: "#e5e7eb transparent",
              }}
              className="campaigns-table-scroll"
            >
              <style>
                {`
                  @media (min-width: 1024px) {
                    .campaigns-table-scroll {
                      overflow-x: hidden !important;
                    }
                  }
                `}
              </style>
              <Table
                sx={{
                  width: "100%",
                  minWidth: { xs: 800, lg: "100%" },
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  tableLayout: { xs: "fixed", lg: "auto" },
                }}
              >
                <TableHead
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    background:
                      "linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)",
                    "& th": {
                      fontWeight: 600,
                      fontSize: "12px",
                      color: "#475569",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      borderBottom: "2px solid #E2E8F0",
                    },
                  }}
                >
                  <TableRow>
                    <TableCell
                      sx={{
                        py: 1.5,
                        px: 2,
                        width: { xs: "40px", lg: "40px" },
                        minWidth: "40px",
                      }}
                    />
                    <TableCell
                      sx={{
                        py: 1.5,
                        px: 2,
                        width: { xs: "180px", lg: "auto" },
                        minWidth: "150px",
                      }}
                    >
                      Title
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1.5,
                        px: 2,
                        textAlign: "center",
                        width: { xs: "100px", lg: "auto" },
                        minWidth: "90px",
                      }}
                    >
                      Applications
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1.5,
                        px: 2,
                        textAlign: "center",
                        width: { xs: "100px", lg: "auto" },
                        minWidth: "90px",
                      }}
                    >
                      Apply By
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1.5,
                        px: 2,
                        textAlign: "center",
                        width: { xs: "100px", lg: "auto" },
                        minWidth: "90px",
                      }}
                    >
                      Delivery
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1.5,
                        px: 2,
                        width: { xs: "120px", lg: "auto" },
                        minWidth: "100px",
                      }}
                    >
                      Category
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1.5,
                        px: 2,
                        textAlign: "center",
                        width: { xs: "90px", lg: "auto" },
                        minWidth: "80px",
                      }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1.5,
                        px: 2,
                        textAlign: "center",
                        width: { xs: "70px", lg: "70px" },
                        minWidth: "60px",
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading && safeCampaignData.length === 0 ? (
                    // Show shimmer rows during initial loading
                    [...Array(8)].map((_, index) => (
                      <TableRowShimmer key={`shimmer-${index}`} index={index} />
                    ))
                  ) : safeCampaignData.length > 0 ? (
                    safeCampaignData.map((item, index) => (
                      <JobCampaignsTable
                        user={user}
                        onStartChat={onStartChat}
                        setOfferSelectedForReview={setOfferSelectedForReview}
                        onClick={() => {
                          setModalCampaigns("detail");
                          setModalDataCampaigns(item);
                        }}
                        onEdit={() => onEditCampaign && onEditCampaign(item)}
                        onDelete={() =>
                          onDeleteCampaign && onDeleteCampaign(item.id)
                        }
                        onRefresh={onRefresh}
                        onApplicationCountClick={(campaignId) =>
                          onApplicationCountClick &&
                          onApplicationCountClick(campaignId, item.title)
                        }
                        key={index}
                        {...item}
                        setTab={setTab}
                        setCampaignId={setCampaignId}
                        setToastList={setToastList}
                        toastList={toastList}
                      />
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        align="center"
                        sx={{
                          py: 6,
                          color: "#6B7280",
                          fontSize: "14px",
                        }}
                      >
                        No campaigns found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {/* Loading indicator for infinite scroll */}
              {isLoadingMore && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "16px",
                    color: "#6B7280",
                    fontSize: "14px",
                    gap: "8px",
                  }}
                >
                  <Loader2
                    size={20}
                    className="animate-spin"
                    style={{ color: "#10B981" }}
                  />
                  Loading more...
                </div>
              )}

              {/* End of data indicator */}
              {!hasMore && safeCampaignData.length > 0 && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "16px",
                    color: "#9CA3AF",
                    fontSize: "13px",
                  }}
                >
                  No more campaigns to load
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {toastList && <Toast list={toastList} setList={setToastList} />}

      {isUserHasSubscription === false && user !== null && (
        <>
          <NotSubscribe
            goToPricing={goToPricing}
            type="brand"
            setAuth={setAuth}
            setModal={setModal}
            NotifModul={NotifModul}
            setNotifModul={setModalNotification}
            profiles={profiles}
            closeCartPanel={closeCartPanel}
            user={user}
            cartItem={cartItem}
            title={"Chat"}
            background={"background_inflist.png"}
          ></NotSubscribe>
        </>
      )}
    </div>
  );
}
